// Generated by DesignToCode

import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChild,
  computed,
  input,
  signal,
  effect,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-generic-input-field',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SvgIconComponent],
  templateUrl: './generic-input-field.html',
})
export class GenericInputField implements OnInit, OnDestroy {
  @ViewChild('inputContainer', { static: true }) inputContainerRef!: ElementRef;

  // Inputs
  public size = input<'small' | 'medium' | 'large'>('medium');
  public formGroup = input.required<FormGroup>();
  public controlName = input.required<string>();
  public placeholder = input<string>('');
  public leftIcon = input<string>('');
  public amountModifier = input<string>('');
  public helper = input<string>('');
  // Inputs for states
  public disabled = input<boolean>(false);
  public readonly = input<boolean>(false);
  // Inputs for Validations
  public showValidationTemplates = input<boolean>(false);
  public errorMessage = input<string>('Error text');
  public successMessage = input<string>('Success text');

  // When it is used in dropdown component
  public isDropdown = input<boolean>(false);
  public dropdownExpanded = input<boolean>(false);

  public errorIcon = signal<string>('icon_error_20px.svg');
  public successIcon = signal<string>('icon_success_20px.svg');
  public dropdownIcon = signal<string>('icon_chevron-down_20px.svg');

  // States
  private isHovered = signal(false);
  private isFilling = signal(false);
  private isFocused = signal(false);
  private isFilled = signal(false);
  public isReadonly = signal(false);

  private destroy$ = new Subject<void>();

  private formState = signal({
    invalid: false,
    dirty: false,
    touched: false,
    valid: false,
  });

  constructor() {
    effect(() => {
      const fc = this.formGroup()?.get(this.controlName());
      if (fc?.value != null && fc?.value !== '') {
        this.isFilled.set(true);
      }
    });
    effect(() => {
      const isReadonlyValue = this.readonly();
      this.isReadonly.set(isReadonlyValue);
      if (this.isDropdown()) {
        this.isReadonly.set(true);
      }
    });
  }

  ngOnInit(): void {
    if (this.control() != null) {
      this.control()
        .statusChanges.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (
            this.formState().dirty !== this.control().dirty ||
            this.formState().invalid !== this.control().invalid ||
            this.formState().touched !== this.control().touched ||
            this.formState().valid !== this.control().valid
          ) {
            this.formState.set({
              invalid: this.control().invalid,
              dirty: this.control().dirty,
              touched: this.control().touched,
              valid: this.control().valid,
            });
          }
        });

      this.control().updateValueAndValidity();
    }

    this.control()
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value != null) {
          this.isFocused.set(false);
          this.isFilling.set(true);
          if (this.isDropdown() && value !== '') {
            this.isFilling.set(false);
            this.isFilled.set(true);
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public control = computed(() => {
    const fc = this.formGroup()?.get(this.controlName());
    return fc as FormControl;
  });

  private hasError = computed(() => {
    const state = this.formState();
    return (
      (state.invalid &&
        state.dirty &&
        state.touched &&
        this.showValidationTemplates() &&
        !this.isFilling()) ||
      false
    );
  });

  private isSuccess = computed(() => {
    const state = this.formState();
    return (
      (state.valid &&
        state.dirty &&
        this.showValidationTemplates() &&
        !this.isFilling()) ||
      false
    );
  });

  // Get error or helper message
  public message = computed(() => {
    if (this.isFilling()) {
      return this.helper();
    } else if (this.hasError()) {
      return this.errorMessage();
    } else if (this.isSuccess()) {
      return this.successMessage();
    }

    return this.helper();
  });

  public onMouseEnter() {
    this.isHovered.set(true);
  }

  public onMouseLeave() {
    this.isHovered.set(false);
  }

  public onFocus() {
    this.isFocused.set(true);
    this.isFilled.set(false);
  }

  public onBlur() {
    this.isFocused.set(false);
    this.isFilling.set(false);
    this.isFilled.set(
      this.control().value != null && this.control().value !== '',
    );
    this.formState.set({
      invalid: this.control().invalid,
      dirty: this.control().dirty,
      touched: this.control().touched,
      valid: this.control().valid,
    });
  }

  public tabIndexInput = computed(() => {
    let tabIndex = 0;
    if (!this.isDropdown()) {
      tabIndex = this.disabled() || this.isReadonly() ? -1 : 0;
    } else {
      tabIndex = this.disabled() ? -1 : 0;
    }
    return tabIndex;
  });

  public onClickElement() {
    if (this.isDropdown()) {
      this.isFocused.set(true);
    }
  }

  public rightIconCalculated = computed(() => {
    if (this.isSuccess()) {
      return this.successIcon();
    }
    if (this.hasError()) {
      return this.errorIcon();
    }
    if (this.isDropdown()) {
      return this.dropdownIcon();
    }

    return '';
  });

  public containerClasses = computed(() => {
    const classes: string[] = [
      'flex',
      'items-center',
      'gap-xs',
      'w-full',
      'transition-all',
      'duration-200',
    ];

    if (this.size() === 'small') {
      classes.push('rounded-2xs', 'px-3xs', 'py-3xs');
    } else if (this.size() === 'medium') {
      classes.push('rounded-xs', 'px-sm', 'py-xs');
    } else if (this.size() === 'large') {
      classes.push('rounded-sm', 'px-sm', 'py-sm');
    }

    if (this.hasError()) {
      classes.push('bg-red-20', 'border', 'border-red-80', 'text-red-120');
    } else if (this.isSuccess()) {
      classes.push(
        'bg-green-20',
        'border',
        'border-green-80',
        'text-green-120',
      );
    } else {
      if (this.isFilling() && !this.disabled()) {
        classes.push(
          'bg-navy-blue-20',
          'border',
          'border-navy-blue-100',
          'text-grey-120',
        );
      } else if (this.isFocused() && !this.disabled()) {
        classes.push(
          'bg-navy-blue-20',
          'border',
          'border-navy-blue-100',
          'text-grey-120',
          'shadow-[0px_0px_0px_3px_rgba(208,221,255,1)]',
        );
      } else if (this.isHovered() && !this.disabled()) {
        classes.push('bg-grey-20', 'border', 'border-grey-90', 'text-grey-90');
      } else if (this.isFilled() && !this.disabled()) {
        classes.push(
          'bg-grey-20',
          'border',
          'border-grey-100',
          'text-grey-120',
        );
      } else if (this.disabled()) {
        classes.push(
          'bg-grey-20',
          'border',
          'border-grey-50',
          'text-grey-80',
          'cursor-not-allowed',
        );
      } else {
        classes.push('bg-grey-20', 'border', 'border-grey-50', 'text-grey-90');
      }
    }

    return classes.join(' ');
  });

  public leftIconClasses = computed(() => {
    const classes: string[] = ['w-xl', 'h-xl'];

    if (this.hasError()) {
      classes.push('fill-red-110');
    } else if (this.isSuccess()) {
      classes.push('fill-green-110');
    } else {
      if (this.isFilling() && !this.disabled()) {
        classes.push('fill-grey-110');
      } else if (this.isFocused() && !this.disabled()) {
        classes.push('fill-grey-110');
      } else if (this.isHovered() && !this.disabled()) {
        classes.push('fill-grey-100');
      } else if (this.isFilled() && !this.disabled()) {
        classes.push('fill-grey-120');
      } else if (this.disabled()) {
        classes.push('fill-grey-80', 'cursor-not-allowed');
      } else {
        classes.push('fill-grey-100');
      }
    }

    return classes.join(' ');
  });

  public inputClasses = computed(() => {
    const classes: string[] = [
      'bg-transparent',
      'w-full',
      'outline-none',
      'font-body',
      'font-medium',
      '[appearance:textfield]',
      '[&::-webkit-inner-spin-button]:appearance-none',
      '[&::-webkit-outer-spin-button]:appearance-none',
    ];

    if (this.size() === 'small') {
      classes.push('text-sm/lg');
    } else {
      classes.push('text-md/xl');
    }

    if (this.hasError()) {
      classes.push('text-red-120', 'placeholder:text-grey-90');
    } else if (this.isSuccess()) {
      classes.push('text-green-120', 'placeholder:text-green-90');
    } else {
      if (this.isFilling() && !this.disabled()) {
        classes.push('text-grey-120', 'placeholder:text-grey-90');
      } else if (this.isFocused() && !this.disabled()) {
        classes.push('text-grey-110', 'placeholder:text-grey-90');
      } else if (this.isHovered() && !this.disabled()) {
        classes.push('text-grey-100', 'placeholder:text-grey-90');
      } else if (this.isFilled() && !this.disabled()) {
        classes.push('text-grey-120', 'placeholder:text-grey-90');
      } else if (this.disabled()) {
        classes.push(
          'text-grey-80',
          'placeholder:text-grey-80',
          'cursor-not-allowed',
        );
      } else {
        classes.push('text-grey-100', 'placeholder:text-grey-90');
      }
    }

    if (!this.disabled()) {
      if (this.isReadonly()) {
        classes.push('cursor-default');
      }
      if (this.isDropdown()) {
        classes.push('cursor-pointer');
      }
    }

    return classes.join(' ');
  });

  public amountModifierClasses = computed(() => {
    const classes: string[] = ['font-body', 'font-medium'];

    if (this.size() === 'small') {
      classes.push('text-sm/lg');
    } else {
      classes.push('text-md/xl');
    }

    if (this.hasError()) {
      classes.push('text-red-120');
    } else if (this.isSuccess()) {
      classes.push('text-green-120');
    } else {
      if (this.isFilling() && !this.disabled()) {
        classes.push('text-grey-110');
      } else if (this.isFocused() && !this.disabled()) {
        classes.push('text-grey-110');
      } else if (this.isHovered() && !this.disabled()) {
        classes.push('text-grey-100');
      } else if (this.isFilled() && !this.disabled()) {
        classes.push('text-grey-120');
      } else if (this.disabled()) {
        classes.push('text-grey-80', 'cursor-not-allowed');
      } else {
        classes.push('text-grey-100');
      }
    }

    return classes.join(' ');
  });

  public rightIconClasses = computed(() => {
    const classes: string[] = ['transition-transform', 'duration-200'];

    if (this.size() === 'small') {
      classes.push('w-lg', 'h-lg');
    } else {
      classes.push('w-xl', 'h-xl');
    }

    if (this.hasError()) {
      classes.push('fill-red-110');
    } else if (this.isSuccess()) {
      classes.push('fill-green-110');
    } else {
      if (this.isFilling() && !this.disabled()) {
        classes.push('fill-grey-120');
      } else if (this.isFocused() && !this.disabled()) {
        classes.push('fill-grey-120');
      } else if (this.isHovered() && !this.disabled()) {
        classes.push('fill-grey-100');
      } else if (this.isFilled() && !this.disabled()) {
        classes.push('fill-grey-120');
      } else if (this.disabled()) {
        classes.push('fill-grey-80', 'cursor-not-allowed');
      } else {
        classes.push('fill-grey-100');
      }
    }

    if (this.dropdownExpanded()) {
      classes.push('rotate-180');
    }

    return classes.join(' ');
  });

  public messageClasses = computed(() => {
    const classes = ['font-body', 'font-medium', 'text-xs', 'pt-3xs'];

    if (this.size() === 'small') {
      classes.push('px-3xs');
    } else {
      classes.push('px-sm');
    }

    if (this.hasError()) {
      classes.push('text-red-120');
    } else if (this.isSuccess()) {
      classes.push('text-green-120');
    } else {
      classes.push('text-grey-100');
    }

    return classes.join(' ');
  });
}
